import { Box, Autocomplete } from "@mui/material";
import StyledTextField from "@/modules/common/components/input/styled-text-field";
import { useState } from "react";
import CustomLabel from "@/modules/common/components/input/custom-label";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";

interface PropertyOption {
  id: string;
  compound: string;
}

interface PropertySelectorWithSmallLabelProps {
  label?: string;
  value: PropertyOption | null;
  onChange: (value: PropertyOption | null) => void;
  options?: PropertyOption[];
  ref?: React.RefObject<HTMLDivElement>;
  onSearch?: (searchTerm: string) => void;
  loading?: boolean;
  [key: string]: any;
}

const PropertySelectorWithSmallLabel = ({
  label,
  value,
  onChange,
  options = [],
  ref,
  onSearch,
  loading = false,
  ...props
}: PropertySelectorWithSmallLabelProps) => {
  const [inputValue, setInputValue] = useState("");

  const customLabelStyle = {
    ...nawyTheme.nawy.typography.english.variants.body.xs.regular,
    color: nawyTheme.nawy.colors.semantic.text.secondary,
  };

  const propertyOptions: PropertyOption[] = options.length > 0 ? options : [];
  return (
    <Box ref={ref} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {label && <CustomLabel sx={customLabelStyle}>{label}</CustomLabel>}
      <Autocomplete
        value={value}
        onChange={(_event, newValue) => onChange(newValue)}
        inputValue={inputValue}
        loading={loading}
        loadingText="Loading..."
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue);
          if (onSearch) {
            onSearch(newInputValue);
          }
        }}
        options={propertyOptions}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.id
        }
        filterOptions={(options) => options}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          return (
            <Box component="li" key={key} {...otherProps}>
              <Box>
                <Box
                  sx={{
                    fontWeight:
                      nawyTheme.nawy.typography.english.variants.body.md
                        .semibold.fontWeight,
                    fontSize:
                      nawyTheme.nawy.typography.english.variants.body.md
                        .semibold.fontSize,
                  }}
                >
                  {option.id}
                </Box>
                <Box
                  sx={{
                    color: nawyTheme.nawy.colors.semantic.text.secondary,
                    fontSize:
                      nawyTheme.nawy.typography.english.variants.body.md.regular
                        .fontSize,
                  }}
                >
                  {option.compound}
                </Box>
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            placeholder="Search properties..."
            variant="outlined"
            size="small"
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                height: "36px",
              },
            }}
          />
        )}
        sx={{
          "& .MuiAutocomplete-inputRoot": {
            paddingRight: "32px !important",
          },
        }}
        {...props}
      />
    </Box>
  );
};

export default PropertySelectorWithSmallLabel;
